import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, AlertTriangle, ChevronDown, BookOpen, Map, Sun, ShoppingCart, RotateCcw, Moon, ArrowRight, ShieldCheck, Zap, Leaf, Clock, HeartPulse, X } from 'lucide-react';
// --- Helper Components ---
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay
}} className={className} data-id="element-2">
    {children}
  </motion.div>;
const CTAButton = ({
  text,
  subtext,
  className = '',
  isCheckout = false
}: {
  text: string;
  subtext?: string;
  className?: string;
  isCheckout?: boolean;
}) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isCheckout) {
      e.preventDefault();
      const checkoutSection = document.getElementById('checkout');
      if (checkoutSection) {
        checkoutSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };
  return <a href={isCheckout ? 'https://cuerposabioo.com/cart/48020563558622:1' : '#checkout'} target={isCheckout ? '_blank' : undefined} rel={isCheckout ? 'noopener noreferrer' : undefined} onClick={handleClick} className={`group flex flex-col items-center justify-center bg-terra-500 hover:bg-terra-600 text-white rounded-full px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${className}`} data-id="element-3">
      <span className="flex items-center text-lg md:text-xl font-bold font-heading tracking-wide" data-id="element-4">
        {text}
        <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-5" />
      </span>
      {subtext && <span className="text-terra-100 text-xs md:text-sm mt-1 font-medium" data-id="element-6">
          {subtext}
        </span>}
    </a>;
};
const AccordionItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-sage-200 last:border-0" data-id="element-7">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center py-5 text-left focus:outline-none group" data-id="element-8">
        <span className="font-heading font-semibold text-lg text-sage-900 group-hover:text-terra-600 transition-colors" data-id="element-9">
          {question}
        </span>
        <ChevronDown className={`w-5 h-5 text-sage-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-10" />
      </button>
      <AnimatePresence data-id="element-11">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-12">
            <p className="pb-5 text-gray-600 leading-relaxed" data-id="element-13">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
const Modal = ({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-14">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose} data-id="element-15">
        <motion.div initial={{
        scale: 0.95,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} exit={{
        scale: 0.95,
        opacity: 0
      }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-16">
          <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-17">
            <h3 className="text-2xl font-bold font-heading text-sage-900" data-id="element-18">
              {title}
            </h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-19">
              <X className="w-5 h-5 text-gray-500" data-id="element-20" />
            </button>
          </div>
          <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-21">
            {children}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
};
// --- Main Page Component ---
export function LandingPage() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  return <main className="min-h-screen bg-sand-50 font-body text-gray-800 selection:bg-sage-200 selection:text-sage-900" data-id="element-22">
      {/* 1. HERO SECTION */}
      <section className="relative pt-12 pb-20 md:pt-20 md:pb-32 overflow-hidden bg-white" data-id="element-23">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-sage-50 via-white to-white opacity-70" data-id="element-24"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-25">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-26">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} className="flex-1 text-center lg:text-left" data-id="element-27">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-sage-100 text-sage-800 text-sm font-semibold mb-6" data-id="element-28">
                <Leaf className="w-4 h-4 mr-2" data-id="element-29" />
                Método Probado de 21 Días
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-sage-900 leading-tight mb-6" data-id="element-30">
                Elimina la Hinchazón y Recupera tu Energía en Solo 21 Días{' '}
                <span className="text-terra-500 italic" data-id="element-31">
                  (Sin Dietas de Hambre)
                </span>
              </h1>
              <h2 className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8 max-w-2xl mx-auto lg:mx-0" data-id="element-32">
                El Protocolo de Eliminación Selectiva diseñado para mujeres y
                hombres de 30+ que están cansados de despertar inflamados,
                agotados y con "neblina mental".
              </h2>
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4" data-id="element-33">
                <CTAButton text="¡Sí! Quiero Apagar la Inflamación Ahora" subtext="Acceso instantáneo en formato PDF" className="w-full sm:w-auto" data-id="element-34" />
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="flex-1 w-full max-w-md lg:max-w-none relative" data-id="element-35">
              <div className="absolute inset-0 bg-sage-200 rounded-3xl transform rotate-3 scale-105 opacity-50" data-id="element-36"></div>
              <img src="https://cdn.magicpatterns.com/uploads/wAmcXF8VkhfU69pGWFiRz8/Desinflamacion_21_dias.png" alt="Adiós Inflamación en 21 Días - Guía Digital" className="relative z-10 w-full h-auto rounded-2xl shadow-2xl" data-id="element-37" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. OPENING HOOK */}
      <section className="py-20 bg-sand-100" data-id="element-38">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center" data-id="element-39">
          <FadeIn data-id="element-40">
            <p className="text-xl md:text-2xl font-heading text-sage-900 leading-relaxed italic" data-id="element-41">
              "Te miras al espejo por la mañana y apenas te reconoces. Tus ojos
              están inflamados, tu abdomen se siente tenso antes de probar el
              primer bocado y esa ropa que te quedaba bien hace dos semanas hoy
              simplemente no cierra."
            </p>
            <div className="w-24 h-1 bg-terra-500 mx-auto mt-8 mb-8 rounded-full" data-id="element-42"></div>
            <p className="text-lg text-gray-700 leading-relaxed" data-id="element-43">
              No es falta de voluntad. No es solo "el paso de los años". Es un
              incendio interno invisible llamado{' '}
              <strong className="text-sage-800" data-id="element-44">inflamación crónica</strong> que
              está drenando tu vitalidad y bloqueando tu metabolismo todos los
              días.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 3. THE PROBLEM */}
      <section className="py-20 bg-white" data-id="element-45">
        <div className="max-w-4xl mx-auto px-4 sm:px-6" data-id="element-46">
          <FadeIn data-id="element-47">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-center text-sage-900 mb-12" data-id="element-48">
              Vives en un estado de emergencia celular constante
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-49">
            <FadeIn delay={0.1} className="bg-sand-50 p-8 rounded-2xl border border-sand-200" data-id="element-50">
              <HeartPulse className="w-10 h-10 text-terra-500 mb-4" data-id="element-51" />
              <p className="text-gray-700 leading-relaxed" data-id="element-52">
                Esa pesadez en las articulaciones al levantarte, el cansancio
                extremo a las 3 de la tarde que solo se calma con café, y la
                frustración de sentirte "hinchado" incluso cuando intentas comer
                saludable.
              </p>
            </FadeIn>
            <FadeIn delay={0.2} className="bg-sand-50 p-8 rounded-2xl border border-sand-200" data-id="element-53">
              <ShieldCheck className="w-10 h-10 text-terra-500 mb-4" data-id="element-54" />
              <p className="text-gray-700 leading-relaxed" data-id="element-55">
                La mayoría de los consejos modernos ignoran que tu cuerpo no
                necesita más restricciones, sino un respiro. Tu sistema inmune
                está atacando sombras porque ya no reconoce qué es comida y qué
                es una amenaza.
              </p>
            </FadeIn>
          </div>
          <FadeIn delay={0.3} className="mt-8 text-center bg-sage-50 p-6 rounded-xl border border-sage-100" data-id="element-56">
            <p className="text-lg font-medium text-sage-800" data-id="element-57">
              Esto te mantiene en un ciclo de retención de líquidos y fatiga del
              que parece imposible salir.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 4. WHY PREVIOUS SOLUTIONS FAILED */}
      <section className="py-20 bg-sage-900 text-sand-50" data-id="element-58">
        <div className="max-w-4xl mx-auto px-4 sm:px-6" data-id="element-59">
          <FadeIn data-id="element-60">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-center mb-16" data-id="element-61">
              Por qué lo que has intentado no funcionó
            </h2>
          </FadeIn>

          <div className="space-y-6" data-id="element-62">
            {[{
            title: 'Dietas Keto o estrictas',
            desc: 'Elevan tu cortisol y terminan inflamándote más a largo plazo.'
          }, {
            title: "Batidos verdes 'detox'",
            desc: 'Suelen estar cargados de oxalatos que irritan un intestino ya sensible.'
          }, {
            title: 'Contar calorías',
            desc: "No sirve de nada si esas calorías provienen de aceites vegetales que mantienen tus células 'en llamas'."
          }, {
            title: 'Ejercicio intenso',
            desc: 'Cuando estás agotado solo profundiza el daño metabólico y la inflamación articular.'
          }, {
            title: 'Suplementos mágicos',
            desc: 'Confiar en ellos es como intentar apagar un incendio forestal con un vaso de agua sin quitar la gasolina.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-63">
                <div className="flex items-start bg-sage-800/50 p-6 rounded-xl border border-sage-700 hover:bg-sage-800 transition-colors" data-id="element-64">
                  <AlertTriangle className="w-6 h-6 text-terra-500 mr-4 flex-shrink-0 mt-1" data-id="element-65" />
                  <div data-id="element-66">
                    <h3 className="text-xl font-bold font-heading text-white mb-2" data-id="element-67">
                      {item.title}
                    </h3>
                    <p className="text-sage-200 leading-relaxed" data-id="element-68">{item.desc}</p>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* 5. THE UNIQUE MECHANISM */}
      <section className="py-24 bg-sand-100 relative overflow-hidden" data-id="element-69">
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-sage-200 rounded-full mix-blend-multiply filter blur-3xl opacity-50 transform -translate-y-1/2 -translate-x-1/2" data-id="element-70"></div>
        <div className="absolute top-1/2 right-0 w-64 h-64 bg-terra-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 transform -translate-y-1/2 translate-x-1/2" data-id="element-71"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-72">
          <FadeIn className="text-center mb-12" data-id="element-73">
            <span className="text-terra-600 font-bold tracking-wider uppercase text-sm mb-4 block" data-id="element-74">
              El Secreto del Éxito
            </span>
            <h2 className="text-3xl md:text-5xl font-bold font-heading text-sage-900 mb-6" data-id="element-75">
              El Protocolo de Eliminación Selectiva (PES)
            </h2>
          </FadeIn>

          <FadeIn delay={0.2} className="bg-white p-8 md:p-12 rounded-3xl shadow-soft border border-sand-200" data-id="element-76">
            <p className="text-xl text-gray-700 leading-relaxed mb-6 font-medium" data-id="element-77">
              No es una dieta, es un{' '}
              <strong className="text-sage-800" data-id="element-78">interruptor biológico</strong>.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-79">
              A diferencia de los planes tradicionales, el PES identifica y
              remueve temporalmente los "disparadores" químicos que mantienen tu
              sistema inmune en alerta máxima. A través de tres fases
              estratégicas, permitimos que tu barrera intestinal se selle y que
              tus niveles de insulina se estabilicen.
            </p>
            <div className="bg-sage-50 border-l-4 border-sage-500 p-6 rounded-r-xl" data-id="element-80">
              <p className="text-sage-900 font-medium italic" data-id="element-81">
                "Al eliminar la fricción digestiva, tu cuerpo deja de retener
                agua por protección y comienza a usar su energía para reparar
                tejidos en lugar de combatir fantasmas alimenticios."
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 6. WHAT YOU GET */}
      <section className="py-20 bg-white" data-id="element-82">
        <div className="max-w-5xl mx-auto px-4 sm:px-6" data-id="element-83">
          <FadeIn data-id="element-84">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-center text-sage-900 mb-16" data-id="element-85">
              Todo lo que recibes hoy
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-86">
            {[{
            icon: BookOpen,
            title: 'Guía Maestra',
            desc: 'El Protocolo de 21 Días paso a paso (Fase de Apagón, Sellado y Blindaje).'
          }, {
            icon: Map,
            title: 'Mapa de Sustitución',
            desc: 'Qué comer exactamente para no sentir privación. Reemplazos directos.'
          }, {
            icon: Sun,
            title: 'Bloqueo Fotónico',
            desc: 'Cómo usar la luz para bajar el cortisol nocturno y descansar profundamente.'
          }, {
            icon: ShoppingCart,
            title: 'Lista de Compras',
            desc: 'Anti-Inflamatoria y sin ingredientes ocultos que arruinen tu progreso.'
          }, {
            icon: RotateCcw,
            title: 'Guía de Reintroducción',
            desc: 'El test exacto para saber qué alimentos son seguros para TU biología.'
          }, {
            icon: Moon,
            title: 'Ritual del Sueño',
            desc: 'El manual para convertir tu habitación en una clínica de sanación nocturna.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} className="bg-sand-50 p-8 rounded-2xl border border-sand-200 hover:shadow-md transition-shadow" data-id="element-87">
                <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center mb-6" data-id="element-88">
                  <item.icon className="w-6 h-6 text-sage-700" data-id="element-89" />
                </div>
                <h3 className="text-xl font-bold font-heading text-sage-900 mb-3" data-id="element-90">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-91">{item.desc}</p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* 7 & 8. WHO IT IS FOR / NOT FOR */}
      <section className="py-20 bg-sand-100" data-id="element-92">
        <div className="max-w-5xl mx-auto px-4 sm:px-6" data-id="element-93">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-94">
            {/* Who is it for */}
            <FadeIn className="bg-white p-8 md:p-10 rounded-3xl shadow-soft border-t-8 border-sage-500" data-id="element-95">
              <h3 className="text-2xl font-bold font-heading text-sage-900 mb-8 flex items-center" data-id="element-96">
                <CheckCircle2 className="w-8 h-8 text-sage-500 mr-3" data-id="element-97" />
                Esto ES para ti si:
              </h3>
              <ul className="space-y-5" data-id="element-98">
                {["Tienes de 30 a 60 años y sientes que tu metabolismo se 'detuvo' de repente.", "Sufres de hinchazón abdominal persistente (el famoso 'gas de las 11 PM').", 'Despiertas con dolor articular o rigidez en las manos y pies.', 'Eres un profesional con fatiga crónica y neblina mental que necesita recuperar su enfoque.', "Has intentado 'comer limpio' pero no ves cambios en tu inflamación facial o corporal."].map((text, i) => <li key={i} className="flex items-start" data-id="element-99">
                    <CheckCircle2 className="w-5 h-5 text-sage-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-100" />
                    <span className="text-gray-700 leading-relaxed" data-id="element-101">
                      {text}
                    </span>
                  </li>)}
              </ul>
            </FadeIn>

            {/* Who is it NOT for */}
            <FadeIn delay={0.2} className="bg-white p-8 md:p-10 rounded-3xl shadow-soft border-t-8 border-terra-500" data-id="element-102">
              <h3 className="text-2xl font-bold font-heading text-gray-900 mb-8 flex items-center" data-id="element-103">
                <XCircle className="w-8 h-8 text-terra-500 mr-3" data-id="element-104" />
                Esto NO es para ti si:
              </h3>
              <ul className="space-y-5" data-id="element-105">
                {['Buscas una pastilla mágica para bajar 10 kilos en una noche.', 'Eres un atleta de alto rendimiento que no está dispuesto a bajar la intensidad del ejercicio por 21 días.', 'No estás dispuesto a leer etiquetas y hacer cambios simples en tu cocina.'].map((text, i) => <li key={i} className="flex items-start" data-id="element-106">
                    <XCircle className="w-5 h-5 text-terra-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-107" />
                    <span className="text-gray-700 leading-relaxed" data-id="element-108">
                      {text}
                    </span>
                  </li>)}
              </ul>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 9. HOW IT WORKS */}
      <section className="py-24 bg-white" data-id="element-109">
        <div className="max-w-4xl mx-auto px-4 sm:px-6" data-id="element-110">
          <FadeIn data-id="element-111">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-center text-sage-900 mb-16" data-id="element-112">
              Tu camino hacia la sanación en 3 fases
            </h2>
          </FadeIn>

          <div className="relative" data-id="element-113">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute left-[28px] top-8 bottom-8 w-0.5 bg-sage-200" data-id="element-114"></div>

            <div className="space-y-12" data-id="element-115">
              {[{
              phase: 'Fase 1',
              days: 'Días 1-7',
              title: 'Fase de Apagón',
              desc: 'Removemos los 5 jinetes de la inflamación para vaciar el exceso de líquidos. Notarás cómo tu rostro y abdomen se deshinchan rápidamente.',
              icon: Zap
            }, {
              phase: 'Fase 2',
              days: 'Días 8-14',
              title: 'Fase de Sellado',
              desc: 'Reparamos la pared intestinal con nutrientes específicos y control circadiano. Tu digestión se vuelve silenciosa y eficiente.',
              icon: ShieldCheck
            }, {
              phase: 'Fase 3',
              days: 'Días 15-21',
              title: 'Fase de Blindaje',
              desc: 'Reintroducimos alimentos bajo prueba y creamos tu escudo metabólico permanente. Descubres exactamente qué tolera tu cuerpo.',
              icon: Leaf
            }].map((step, i) => <FadeIn key={i} delay={i * 0.1} className="relative flex flex-col md:flex-row gap-6 md:gap-10" data-id="element-116">
                  <div className="flex items-center md:items-start z-10" data-id="element-117">
                    <div className="w-14 h-14 rounded-full bg-sage-600 text-white flex items-center justify-center shadow-lg border-4 border-white flex-shrink-0" data-id="element-118">
                      <step.icon className="w-6 h-6" data-id="element-119" />
                    </div>
                    <div className="md:hidden ml-4" data-id="element-120">
                      <span className="text-sm font-bold text-terra-600 uppercase tracking-wider" data-id="element-121">
                        {step.phase}
                      </span>
                      <span className="text-sm text-gray-500 ml-2" data-id="element-122">
                        ({step.days})
                      </span>
                    </div>
                  </div>

                  <div className="bg-sand-50 p-8 rounded-2xl border border-sand-200 flex-1" data-id="element-123">
                    <div className="hidden md:block mb-2" data-id="element-124">
                      <span className="text-sm font-bold text-terra-600 uppercase tracking-wider" data-id="element-125">
                        {step.phase}
                      </span>
                      <span className="text-sm text-gray-500 ml-2" data-id="element-126">
                        ({step.days})
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold font-heading text-sage-900 mb-3" data-id="element-127">
                      {step.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-128">{step.desc}</p>
                  </div>
                </FadeIn>)}
            </div>
          </div>
        </div>
      </section>

      {/* 10. PROOF / BELIEVABILITY */}
      <section className="py-20 bg-sage-50 border-y border-sage-100" data-id="element-129">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center" data-id="element-130">
          <FadeIn data-id="element-131">
            <Clock className="w-12 h-12 text-sage-500 mx-auto mb-6" data-id="element-132" />
            <p className="text-2xl font-heading text-sage-900 leading-relaxed mb-8" data-id="element-133">
              "En los primeros 7 días de este protocolo, la mayoría de los
              usuarios reportan una disminución visible en la hinchazón del
              rostro, mayor claridad mental al despertar y la desaparición de la
              pesadez estomacal después de las comidas."
            </p>
            <p className="text-lg text-gray-600 font-medium" data-id="element-134">
              No es magia, es lo que sucede cuando dejas de agredir a tu sistema
              inmunológico en cada bocado.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 11. FAQ */}
      <section className="py-24 bg-white" data-id="element-135">
        <div className="max-w-3xl mx-auto px-4 sm:px-6" data-id="element-136">
          <FadeIn data-id="element-137">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-center text-sage-900 mb-12" data-id="element-138">
              Preguntas Frecuentes
            </h2>
          </FadeIn>

          <FadeIn delay={0.2} className="bg-white rounded-2xl shadow-sm border border-sage-100 p-6 md:p-8" data-id="element-139">
            <AccordionItem question="¿Pasaré hambre?" answer="No, el protocolo incluye grasas saludables y proteínas saciantes que eliminan la ansiedad por el azúcar. El objetivo es sanar, no restringir calorías." data-id="element-140" />
            <AccordionItem question="¿Es caro seguir este plan?" answer="Al contrario, ahorrarás dinero al eliminar alimentos procesados, snacks empaquetados y suplementos innecesarios que no estaban funcionando." data-id="element-141" />
            <AccordionItem question="¿Puedo hacerlo si trabajo mucho?" answer="Sí, está diseñado para personas con vidas ocupadas que necesitan resultados sin complicaciones ni rutinas extremas." data-id="element-142" />
            <AccordionItem question="¿Tengo que cocinar horas?" answer="No, las preparaciones son simples, basadas en alimentos reales y rápidos de preparar. Te damos opciones prácticas." data-id="element-143" />
            <AccordionItem question="¿Qué pasa después de los 21 días?" answer="Tendrás un manual de vida para saber exactamente qué te inflama y qué te da energía. Conocerás tu cuerpo mejor que nunca." data-id="element-144" />
            <AccordionItem question="¿Puedo tomar café?" answer="Te enseñamos cómo tomarlo de forma estratégica para que no afecte tu inflamación ni dispare tu cortisol." data-id="element-145" />
          </FadeIn>
        </div>
      </section>

      {/* 12. OFFER + PRICE BLOCK */}
      <section id="checkout" className="py-24 bg-sage-900 text-white relative overflow-hidden" data-id="element-146">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" data-id="element-147"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10 text-center" data-id="element-148">
          <FadeIn data-id="element-149">
            <h2 className="text-3xl md:text-5xl font-bold font-heading mb-6" data-id="element-150">
              Recupera tu ligereza, tu energía y tu cuerpo.
            </h2>
            <p className="text-xl text-sage-200 mb-12 max-w-2xl mx-auto" data-id="element-151">
              Deja de adivinar y comienza a sanar desde la raíz con un método
              probado y directo al grano.
            </p>

            <div className="bg-white text-gray-900 rounded-3xl p-8 md:p-12 max-w-2xl mx-auto shadow-2xl transform hover:scale-[1.02] transition-transform duration-300" data-id="element-152">
              <h3 className="text-2xl font-bold font-heading text-sage-900 mb-2" data-id="element-153">
                Sistema Completo "Adiós Inflamación en 21 Días"
              </h3>
              <p className="text-gray-500 mb-8" data-id="element-154">
                Acceso instantáneo de por vida
              </p>

              <div className="mb-4" data-id="element-155">
                <div className="flex justify-center items-baseline gap-3 mb-2" data-id="element-156">
                  <span className="text-3xl font-bold text-gray-400 line-through" data-id="element-157">
                    $19.99
                  </span>
                  <span className="bg-terra-500 text-white px-3 py-1 rounded-full text-sm font-bold" data-id="element-158">
                    68% OFF
                  </span>
                </div>
                <div className="flex justify-center items-baseline" data-id="element-159">
                  <span className="text-6xl font-black text-sage-900 tracking-tight" data-id="element-160">
                    $6.39
                  </span>
                  <span className="text-xl text-gray-500 ml-2 font-medium" data-id="element-161">
                    USD
                  </span>
                </div>
              </div>

              <p className="text-sm text-gray-500 uppercase tracking-wide font-bold mb-8" data-id="element-162">
                Pago Único • Sin Suscripciones
              </p>

              <CTAButton text="¡Sí! Quiero Apagar la Inflamación Ahora" subtext="Acceso instantáneo en formato PDF para leer en cualquier dispositivo" className="w-full" isCheckout={true} data-id="element-163" />

              <div className="mt-6 flex items-center justify-center text-sm text-gray-500" data-id="element-164">
                <ShieldCheck className="w-4 h-4 mr-2" data-id="element-165" />
                Pago 100% Seguro y Encriptado
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-sand-100 py-12 text-center text-gray-500 text-sm border-t border-sand-200" data-id="element-166">
        <div className="max-w-4xl mx-auto px-4" data-id="element-167">
          <div className="flex justify-center gap-6 mb-6" data-id="element-168">
            <button onClick={() => setShowPrivacy(true)} className="text-sage-700 hover:text-sage-900 font-medium transition-colors underline" data-id="element-169">
              Política de Privacidad
            </button>
            <span className="text-gray-300" data-id="element-170">|</span>
            <button onClick={() => setShowTerms(true)} className="text-sage-700 hover:text-sage-900 font-medium transition-colors underline" data-id="element-171">
              Términos y Condiciones
            </button>
          </div>
          <p data-id="element-172">
            © {new Date().getFullYear()} Adiós Inflamación. Todos los derechos
            reservados.
          </p>
          <p className="mt-2 text-xs text-gray-400" data-id="element-173">
            Este producto no sustituye el consejo médico profesional. Consulta a
            tu médico antes de iniciar cualquier protocolo de salud.
          </p>
        </div>
      </footer>

      {/* MODALS */}
      <Modal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Política de Privacidad" data-id="element-174">
        <div className="prose prose-sage max-w-none" data-id="element-175">
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-176">
            En Adiós Inflamación, nos comprometemos a proteger tu privacidad y
            la seguridad de tu información personal.
          </p>

          <h4 className="text-lg font-bold text-sage-900 mt-6 mb-3" data-id="element-177">
            Información que Recopilamos
          </h4>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-178">
            Recopilamos información que nos proporcionas directamente al
            realizar una compra, incluyendo tu nombre, dirección de correo
            electrónico y datos de pago necesarios para procesar tu pedido.
          </p>

          <h4 className="text-lg font-bold text-sage-900 mt-6 mb-3" data-id="element-179">
            Uso de la Información
          </h4>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-180">
            Utilizamos tu información para:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4" data-id="element-181">
            <li data-id="element-182">Procesar y entregar tu compra del producto digital</li>
            <li data-id="element-183">
              Enviarte confirmaciones de pedido y actualizaciones importantes
            </li>
            <li data-id="element-184">Proporcionar soporte al cliente cuando lo necesites</li>
            <li data-id="element-185">Mejorar nuestros productos y servicios</li>
          </ul>

          <h4 className="text-lg font-bold text-sage-900 mt-6 mb-3" data-id="element-186">
            Protección de Datos
          </h4>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-187">
            Implementamos medidas de seguridad técnicas y organizativas para
            proteger tu información personal contra acceso no autorizado,
            pérdida o alteración. Todos los pagos se procesan a través de
            plataformas seguras y encriptadas.
          </p>

          <h4 className="text-lg font-bold text-sage-900 mt-6 mb-3" data-id="element-188">
            Compartir Información
          </h4>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-189">
            No vendemos, alquilamos ni compartimos tu información personal con
            terceros para fines de marketing. Solo compartimos información con
            proveedores de servicios necesarios para procesar tu compra
            (procesadores de pago, plataformas de entrega de contenido digital).
          </p>

          <h4 className="text-lg font-bold text-sage-900 mt-6 mb-3" data-id="element-190">
            Tus Derechos
          </h4>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-191">
            Tienes derecho a acceder, corregir o eliminar tu información
            personal en cualquier momento. Para ejercer estos derechos,
            contáctanos a través de nuestro correo de soporte.
          </p>

          <h4 className="text-lg font-bold text-sage-900 mt-6 mb-3" data-id="element-192">Cookies</h4>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-193">
            Utilizamos cookies esenciales para el funcionamiento del sitio web y
            para mejorar tu experiencia de navegación. No utilizamos cookies de
            seguimiento de terceros.
          </p>

          <p className="text-gray-600 leading-relaxed mt-6 text-sm" data-id="element-194">
            Última actualización:{' '}
            {new Date().toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </div>
      </Modal>

      <Modal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Términos y Condiciones" data-id="element-195">
        <div className="prose prose-sage max-w-none" data-id="element-196">
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-197">
            Al adquirir el producto "Adiós Inflamación en 21 Días", aceptas los
            siguientes términos y condiciones.
          </p>

          <h4 className="text-lg font-bold text-sage-900 mt-6 mb-3" data-id="element-198">
            Naturaleza del Producto
          </h4>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-199">
            Este es un producto digital en formato PDF que recibirás acceso
            inmediato después de completar tu compra. El protocolo está diseñado
            con fines educativos e informativos sobre nutrición y bienestar.
          </p>

          <h4 className="text-lg font-bold text-sage-900 mt-6 mb-3" data-id="element-200">
            Descargo de Responsabilidad Médica
          </h4>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-201">
            <strong data-id="element-202">IMPORTANTE:</strong> Este producto NO sustituye el consejo,
            diagnóstico o tratamiento médico profesional. Siempre consulta con
            tu médico o profesional de la salud calificado antes de iniciar
            cualquier protocolo de alimentación, especialmente si:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4" data-id="element-203">
            <li data-id="element-204">Tienes condiciones médicas preexistentes</li>
            <li data-id="element-205">Estás tomando medicamentos</li>
            <li data-id="element-206">Estás embarazada o en período de lactancia</li>
            <li data-id="element-207">Tienes alergias alimentarias conocidas</li>
          </ul>

          <h4 className="text-lg font-bold text-sage-900 mt-6 mb-3" data-id="element-208">
            Resultados
          </h4>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-209">
            Los resultados individuales pueden variar. No garantizamos
            resultados específicos, ya que cada persona responde de manera
            diferente según su biología, adherencia al protocolo y condiciones
            de salud individuales.
          </p>

          <h4 className="text-lg font-bold text-sage-900 mt-6 mb-3" data-id="element-210">
            Política de Reembolso
          </h4>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-211">
            Debido a la naturaleza digital del producto y el acceso inmediato al
            contenido completo, todas las ventas son finales. No ofrecemos
            reembolsos una vez que se ha proporcionado acceso al material.
          </p>

          <h4 className="text-lg font-bold text-sage-900 mt-6 mb-3" data-id="element-212">
            Propiedad Intelectual
          </h4>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-213">
            Todo el contenido del producto, incluyendo textos, imágenes,
            gráficos y diseño, está protegido por derechos de autor. La compra
            te otorga una licencia personal, no transferible, para uso
            individual. Está prohibido:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4" data-id="element-214">
            <li data-id="element-215">Compartir, distribuir o revender el contenido</li>
            <li data-id="element-216">Reproducir o copiar el material para uso comercial</li>
            <li data-id="element-217">Modificar o crear trabajos derivados sin autorización</li>
          </ul>

          <h4 className="text-lg font-bold text-sage-900 mt-6 mb-3" data-id="element-218">
            Uso Aceptable
          </h4>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-219">
            Te comprometes a utilizar este producto de manera responsable y
            ética, siguiendo las recomendaciones de manera informada y
            consultando con profesionales de la salud cuando sea necesario.
          </p>

          <h4 className="text-lg font-bold text-sage-900 mt-6 mb-3" data-id="element-220">
            Limitación de Responsabilidad
          </h4>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-221">
            No nos hacemos responsables de cualquier daño, lesión o efecto
            adverso que pueda resultar del uso o mal uso de la información
            proporcionada en este producto. El uso de este material es bajo tu
            propio riesgo y responsabilidad.
          </p>

          <h4 className="text-lg font-bold text-sage-900 mt-6 mb-3" data-id="element-222">
            Modificaciones
          </h4>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-223">
            Nos reservamos el derecho de actualizar estos términos en cualquier
            momento. Los cambios entrarán en vigor inmediatamente después de su
            publicación.
          </p>

          <h4 className="text-lg font-bold text-sage-900 mt-6 mb-3" data-id="element-224">
            Contacto
          </h4>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-225">
            Si tienes preguntas sobre estos términos, por favor contáctanos
            antes de realizar tu compra.
          </p>

          <p className="text-gray-600 leading-relaxed mt-6 text-sm" data-id="element-226">
            Última actualización:{' '}
            {new Date().toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </div>
      </Modal>
    </main>;
}